/**
 * Utilitário para gerar markdown a partir de dados tabulares
 */

export class MarkdownGenerator {
  /**
   * Converte dados tabulares para markdown
   * @param {Object} data - {headers: Array, rows: Array}
   * @param {string} title - Título da tabela (opcional)
   * @returns {string} - Markdown formatado
   */
  static tableToMarkdown(data, title = '') {
    if (!data || !data.headers || !data.rows || data.rows.length === 0) {
      return '';
    }

    let markdown = '';

    if (title) {
      markdown += `# ${title}\n\n`;
    }

    const columnNames = data.headers.map(header => 
      typeof header === 'string' ? header : header.text
    );

    const columnKeys = data.headers.map(header => 
      typeof header === 'string' ? header : header.value
    );

    markdown += `| ${columnNames.join(' | ')} |\n`;
    markdown += `|${columnNames.map(() => '---').join('|')}|\n`;

    data.rows.forEach((row) => {
      const rowValues = columnKeys.map(key => {
        const value = row[key];
        return this.formatCellValue(value);
      });
      markdown += `| ${rowValues.join(' | ')} |\n`;
    });

    return markdown;
  }

  /**
   * Formata o valor de uma célula
   * @param {any} value - Valor da célula
   * @returns {string} - Valor formatado
   */
  static formatCellValue(value) {
    if (value === null || value === undefined) {
      return '-';
    }

    if (typeof value === 'number') {
      // Se for grande (possível valor monetário), formata como moeda
      if (Math.abs(value) >= 1000) {
        return value.toLocaleString('pt-BR', {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2
        });
      }
      // Números pequenos ficam normais
      return value.toString();
    }

    return String(value);
  }

  /**
   * Converte markdown para HTML simples (sem dependências externas)
   * Suporta: Headers, negrito, itálico, links, listas, tabelas
   * @param {string} markdown - Texto em markdown
   * @returns {string} - HTML
   */
  static markdownToHtml(markdown) {
    if (!markdown) {
      return '';
    }

    let html = markdown;

    // Headers
    html = html.replace(/^### (.*?)$/gm, '<h3>$1</h3>');
    html = html.replace(/^## (.*?)$/gm, '<h2>$1</h2>');
    html = html.replace(/^# (.*?)$/gm, '<h1>$1</h1>');

    // Negrito
    html = html.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>');

    // Itálico
    html = html.replace(/\*(.*?)\*/g, '<em>$1</em>');

    // Links
    html = html.replace(/\[(.*?)\]\((.*?)\)/g, '<a href="$2" target="_blank">$1</a>');

    // Tabelas markdown simples
    html = this.convertMarkdownTablesToHtml(html);

    // Compacta o HTML interno das tabelas (remove \n internos) e garante \n\n ao redor
    html = html.replace(/<div class="markdown-table-wrapper">[\s\S]*?<\/div>/g, match =>
      '\n\n' + match.replace(/\n\s*/g, '') + '\n\n'
    );
    // Normaliza sequências de mais de 2 \n geradas pela compactação
    html = html.replace(/\n{3,}/g, '\n\n');

    // Listas (linhas iniciando com "- ")
    html = html.replace(/((?:^- .+\n?)+)/gm, (match) => {
      const items = match.trim().split('\n').map(line => {
        const content = line.replace(/^- /, '');
        return `<li>${content}</li>`;
      });
      return `<ul>${items.join('')}</ul>`;
    });

    // Quebras de linha
    html = html.replace(/\n\n/g, '</p><p>');
    html = '<p>' + html + '</p>';
    html = html.replace(/<p><h/g, '<h');
    html = html.replace(/(<\/h\d>)<\/p>/g, '$1');
    html = html.replace(/<p><ul>/g, '<ul>');
    html = html.replace(/<\/ul><\/p>/g, '</ul>');
    html = html.replace(/<p><table/g, '<table');
    html = html.replace(/<\/table><\/p>/g, '</table>');
    html = html.replace(/<p>(<div class="markdown-table-wrapper">)/g, '$1');
    html = html.replace(/(<\/div>)<\/p>/g, '$1');

    return html;
  }

  /**
   * Converte tabelas em markdown para HTML
   * @param {string} html - HTML contendo markdown de tabelas
   * @returns {string} - HTML com tabelas convertidas
   */
  static convertMarkdownTablesToHtml(html) {
    // Regex para detectar tabelas markdown
    const tableRegex = /\|(.+)\|\n\|[\s\-|:]+\|\n((?:\|.+\|\n?)*)/g;

    return html.replace(tableRegex, (match, header, rows) => {
      const headers = header.split('|').map(h => h.trim()).filter(h => h);
      const rowArray = rows
        .split('\n')
        .filter(r => r.trim())
        .map(r => r.split('|').map(cell => cell.trim()).filter(cell => cell));

      if (rowArray.length === 0) return match;

      // Monta tabela HTML dentro de um wrapper com scroll
      let tableHtml = '<div class="markdown-table-wrapper">\n';
      tableHtml += '  <table class="markdown-table">\n';
      tableHtml += '    <thead>\n      <tr>\n';
      headers.forEach(h => {
        tableHtml += `        <th>${h}</th>\n`;
      });
      tableHtml += '      </tr>\n    </thead>\n';
      tableHtml += '    <tbody>\n';

      rowArray.forEach(row => {
        tableHtml += '      <tr>\n';
        row.forEach(cell => {
          tableHtml += `        <td>${cell}</td>\n`;
        });
        tableHtml += '      </tr>\n';
      });

      tableHtml += '    </tbody>\n  </table>\n</div>';

      return tableHtml;
    });
  }
}
